'use client'

import { useState } from 'react'
import { Shield, AlertTriangle, Users } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useColaboradoresDashboard, useColaboradoresPayrollTrend, type EmployeeListItem } from '@/api/colaboradores-hooks'
import { HeadcountKpiCards } from './components/headcount-kpi-cards'
import { ContractDistributionChart } from './components/contract-distribution-chart'
import { PayrollTrendChart } from './components/payroll-trend-chart'
import { DepartmentHeadcountChart } from './components/department-headcount-chart'
import { EmployeeDataTable } from './components/employee-data-table'
import { EmployeeDetailDrawer } from './components/employee-detail-drawer'

const ALLOWED_ROLES = ['admin', 'rh', 'diretor-tecnico', 'diretor-financeiro']

export default function PainelColaboradoresPage() {
  const { user } = useAuth()

  // RBAC guard — role-based access
  if (!user || !ALLOWED_ROLES.includes(user.role)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in-up">
        <Shield className="h-16 w-16 text-red-500 animate-pulse" />
        <h1 className="text-3xl font-bold text-white font-display">Acesso Restrito</h1>
        <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
          Você não tem permissão para acessar o Painel de Colaboradores.
          Entre em contato com o administrador para solicitar acesso.
        </p>
      </div>
    )
  }

  return <PainelColaboradoresContent />
}

function PainelColaboradoresContent() {
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListItem | null>(null)

  // Dashboard data — headcount, latest payroll, contract/department distributions
  const {
    data: dashboard,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useColaboradoresDashboard()

  // Payroll trend — 12 months of cprov/crel data
  const {
    data: payrollTrend,
    isLoading: trendLoading,
    error: trendError,
  } = useColaboradoresPayrollTrend({ months: 12 })

  // Connection error state — RDS may not be running
  const connectionError = dashboardError || trendError
  const isConnectionError =
    connectionError?.message?.includes('connection') ||
    connectionError?.message?.includes('unavailable') ||
    connectionError?.message?.includes('timeout')

  if (isConnectionError) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="flex flex-col items-center justify-center min-h-[40vh] space-y-4">
          <AlertTriangle className="h-12 w-12 text-yellow-500" />
          <h2 className="text-xl font-semibold text-white font-display">Dados Indisponíveis</h2>
          <p className="text-brand-gray text-center max-w-md font-body">
            Não foi possível conectar ao banco de dados PainelGerencial.
            O servidor RDS pode não estar ativo no momento. Tente novamente mais tarde.
          </p>
        </div>
      </div>
    )
  }

  const isLoading = dashboardLoading || trendLoading

  return (
    <div className="space-y-6">
      <PageHeader />

      {/* KPI Cards — 4-column grid at desktop, 2-column at tablet */}
      <HeadcountKpiCards data={dashboard} isLoading={isLoading} />

      {/* Charts row — pie chart + line chart side by side at desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ContractDistributionChart
          data={dashboard?.contractDistribution}
          isLoading={dashboardLoading}
        />
        <PayrollTrendChart
          data={payrollTrend?.months}
          isLoading={trendLoading}
        />
      </div>

      {/* Department bar chart — full width */}
      <DepartmentHeadcountChart
        data={dashboard?.departmentDistribution}
        isLoading={dashboardLoading}
      />

      {/* Employee data table */}
      <EmployeeDataTable onSelectEmployee={setSelectedEmployee} />

      {/* Employee detail drawer */}
      {selectedEmployee && (
        <EmployeeDetailDrawer
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      )}
    </div>
  )
}

function PageHeader() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-monumenta bg-brand-primary/10 flex items-center justify-center">
        <Users className="w-5 h-5 text-brand-primary" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-white font-display">Painel de Colaboradores</h1>
        <p className="text-brand-gray font-body text-sm">Visão geral da força de trabalho — dados atualizados diariamente</p>
      </div>
    </div>
  )
}
